import React, { useState, useEffect } from 'react';
import {
  Table, Button, Input, Select, Space, Modal, Form, message,
  Card, Row, Col, Statistic, Tag, Badge, Tooltip, Popconfirm,
  DatePicker, Drawer, Typography, Alert, Switch
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  UserOutlined, AuditOutlined, EyeOutlined, KeyOutlined,
  ExclamationCircleOutlined, ReloadOutlined, SettingOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import usuariosAdminService from '../services/usuariosAdminService';
import UsuarioModal from './UsuarioModal';
import AuditoriaDrawer from '../components/AuditoriaDrawer';
import SesionesActivasModal from '../components/SesionesActivasModal';
import './UsuariosAdminPage.css';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;


const UsuariosAdminPage = () => {
  const { user, hasPermission } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    search: '',
    rol: '',
    activo: ''
  });

  // Estados para modales y drawers
  const [usuarioModal, setUsuarioModal] = useState({
    visible: false,
    usuario: null,
    mode: 'create'
  });
  const [auditoriaDrawer, setAuditoriaDrawer] = useState(false);
  const [sesionesModal, setSesionesModal] = useState(false);

  // Estados para estadísticas
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    activos: 0,
    super_admins: 0,
    sesiones_activas: 0
  });

  useEffect(() => {
    cargarUsuarios();
  }, [pagination.current, pagination.pageSize, filtros]);

  const cargarUsuarios = async () => {
    setLoading(true);
    try {
      const response = await usuariosAdminService.obtenerUsuarios({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filtros
      });

      setUsuarios(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));

      // Calcular estadísticas
      const stats = response.data.reduce((acc, usuario) => {
        acc.total++;
        if (usuario.activo) acc.activos++;
        if (usuario.rol === 'super_admin') acc.super_admins++;
        acc.sesiones_activas += usuario.sesiones_activas || 0;
        return acc;
      }, { total: 0, activos: 0, super_admins: 0, sesiones_activas: 0 });

      setEstadisticas(stats);
    } catch (error) {
      message.error('Error al cargar usuarios');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (key, value) => {
    setFiltros(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (paginationInfo) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }));
  };

  const abrirUsuarioModal = (usuario = null, mode = 'create') => {
    setUsuarioModal({
      visible: true,
      usuario,
      mode
    });
  };

  const cerrarUsuarioModal = () => {
    setUsuarioModal({
      visible: false,
      usuario: null,
      mode: 'create'
    });
  };

  const handleUsuarioGuardado = () => {
    cerrarUsuarioModal();
    cargarUsuarios();
    message.success(
      usuarioModal.mode === 'create' 
        ? 'Usuario creado exitosamente'
        : 'Usuario actualizado exitosamente'
    );
  };

  const handleEliminarUsuario = async (id) => {
    try {
      await usuariosAdminService.eliminarUsuario(id);
      cargarUsuarios();
      message.success('Usuario eliminado exitosamente');
    } catch (error) {
      message.error('Error al eliminar usuario');
      console.error('Error:', error);
    }
  };

  const handleResetearPassword = async (id) => {
    Modal.confirm({
      title: 'Resetear Contraseña',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>¿Estás seguro de que deseas resetear la contraseña de este usuario?</p>
          <p><strong>Nota:</strong> El usuario deberá cambiar su contraseña en el próximo inicio de sesión.</p>
        </div>
      ),
      onOk: async () => {
        try {
          // Generar contraseña temporal
          const passwordTemporal = `Horeb${Math.random().toString(36).slice(-8)}!`;
          
          await usuariosAdminService.resetearPassword(id, passwordTemporal);
          
          Modal.info({
            title: 'Contraseña Reseteada',
            content: (
              <div>
                <p>La contraseña ha sido reseteada exitosamente.</p>
                <p><strong>Contraseña temporal:</strong> <code>{passwordTemporal}</code></p>
                <p><strong>Importante:</strong> Comunica esta contraseña al usuario de forma segura.</p>
                <Alert
                  message="Seguridad"
                  description="Por seguridad, esta contraseña temporal solo se muestra una vez. El usuario debe cambiarla en su primer acceso."
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              </div>
            ),
            width: 500,
            okText: 'Entendido'
          });
          
          cargarUsuarios();
        } catch (error) {
          message.error('Error al resetear contraseña');
          console.error('Error:', error);
        }
      }
    });
  };

  const getRoleBadge = (rol) => {
    const roleConfig = {
      super_admin: { color: 'red', text: 'Super Admin' },
      admin_conjuntos: { color: 'blue', text: 'Admin Conjuntos' },
      admin_lectura: { color: 'green', text: 'Solo Lectura' }
    };
    
    const config = roleConfig[rol] || { color: 'default', text: rol };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getEstadoBadge = (activo, requiere_cambio_password) => {
    if (!activo) {
      return <Badge status="error" text="Inactivo" />;
    }
    if (requiere_cambio_password) {
      return <Badge status="warning" text="Cambio de contraseña pendiente" />;
    }
    return <Badge status="success" text="Activo" />;
  };

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      key: 'usuario',
      sorter: true,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.nombre_persona}
          </Text>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      render: getRoleBadge,
      filters: [
        { text: 'Super Admin', value: 'super_admin' },
        { text: 'Admin Conjuntos', value: 'admin_conjuntos' },
        { text: 'Solo Lectura', value: 'admin_lectura' }
      ]
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, record) => getEstadoBadge(record.activo, record.requiere_cambio_password)
    },
    {
      title: 'Sesiones',
      dataIndex: 'sesiones_activas',
      key: 'sesiones_activas',
      align: 'center',
      render: (sesiones) => (
        <Badge 
          count={sesiones || 0} 
          style={{ backgroundColor: sesiones > 0 ? '#52c41a' : '#d9d9d9' }}
        />
      )
    },
    {
      title: 'Último Acceso',
      dataIndex: 'ultimo_acceso',
      key: 'ultimo_acceso',
      render: (fecha) => (
        fecha ? (
          <Tooltip title={new Date(fecha).toLocaleString()}>
            <Text type="secondary">
              {new Date(fecha).toLocaleDateString()}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">Nunca</Text>
        )
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          {hasPermission('usuarios', 'ver') && (
            <Tooltip title="Ver detalles">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => abrirUsuarioModal(record, 'view')}
              />
            </Tooltip>
          )}
          
          {hasPermission('usuarios', 'editar') && (
            <Tooltip title="Editar">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => abrirUsuarioModal(record, 'edit')}
              />
            </Tooltip>
          )}
          
          {hasPermission('usuarios', 'editar') && (
            <Tooltip title="Resetear contraseña">
              <Button
                type="text"
                icon={<KeyOutlined />}
                onClick={() => handleResetearPassword(record.id)}
              />
            </Tooltip>
          )}
          
          {hasPermission('usuarios', 'eliminar') && record.id !== user.id && (
            <Popconfirm
              title="¿Eliminar usuario?"
              description="Esta acción desactivará el usuario y cerrará sus sesiones."
              onConfirm={() => handleEliminarUsuario(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okType="danger"
            >
              <Tooltip title="Eliminar">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>

            
          )}
        </Space>
        
      )
    }
  ];

  return (
    <div className="usuarios-admin-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <Title level={2} style={{ margin: 0 }}>
              <UserOutlined /> Usuarios Administradores
            </Title>
            <Text type="secondary">
              Gestión de usuarios con acceso al portal central
            </Text>
          </div>
          
          <Space>
            {hasPermission('auditoria', 'ver') && (
                <Button
                icon={<AuditOutlined />}
                onClick={() => setAuditoriaDrawer(true)}
                >
                Auditoría
                </Button>
            )}
            
            {hasPermission('usuarios', 'ver') && (
                <Button
                icon={<SettingOutlined />}
                onClick={() => setSesionesModal(true)}
                >
                Sesiones Activas
                </Button>
            )}
            
            {hasPermission('usuarios', 'crear') && (
                <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => abrirUsuarioModal()}
                >
                Nuevo Usuario
                </Button>
            )}
            </Space>
        </div>
      </div>

      {/* Estadísticas */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Usuarios"
              value={estadisticas.total}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Usuarios Activos"
              value={estadisticas.activos}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Super Admins"
              value={estadisticas.super_admins}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Sesiones Activas"
              value={estadisticas.sesiones_activas}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Buscar por usuario, nombre o email"
              value={filtros.search}
              onChange={(e) => handleFiltroChange('search', e.target.value)}
              onSearch={() => cargarUsuarios()}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Select
              placeholder="Filtrar por rol"
              value={filtros.rol}
              onChange={(value) => handleFiltroChange('rol', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="super_admin">Super Admin</Option>
              <Option value="admin_conjuntos">Admin Conjuntos</Option>
              <Option value="admin_lectura">Solo Lectura</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Select
              placeholder="Estado"
              value={filtros.activo}
              onChange={(value) => handleFiltroChange('activo', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="true">Activos</Option>
              <Option value="false">Inactivos</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={cargarUsuarios}
                loading={loading}
              >
                Actualizar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabla */}
      <Card>
        <Table
          columns={columns}
          dataSource={usuarios}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} usuarios`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modal de Usuario */}
      <UsuarioModal
        visible={usuarioModal.visible}
        usuario={usuarioModal.usuario}
        mode={usuarioModal.mode}
        onCancel={cerrarUsuarioModal}
        onSuccess={handleUsuarioGuardado}
      />

      {/* Drawer de Auditoría */}
      <AuditoriaDrawer
        visible={auditoriaDrawer}
        onClose={() => setAuditoriaDrawer(false)}
      />

      {/* Modal de Sesiones Activas */}
      <SesionesActivasModal
        visible={sesionesModal}
        onCancel={() => setSesionesModal(false)}
        onSessionClosed={cargarUsuarios}
      />
    </div>
  );
};

export default UsuariosAdminPage;